type ShippingMethodMappingBrand = {
  [brand: string]: number[] | string[] | boolean | undefined;
  default?: string[]; // Array of locale strings
  isDefault?: boolean;
};

interface ShippingMethodMappingGroup {
  [shippingMethodKey: string]: ShippingMethodMappingBrand;
}

interface ShippingMethodMappingData {
  PUBLIC: ShippingMethodMappingGroup;
  PRIVATE: ShippingMethodMappingGroup;
}


// Hardcoded store for this repo
const STORE = 'PUBLIC';

/**
 * Determines the current environment based on the hostname
 */
function getEnvironment(): 'stage' | 'prod' {
  if (window.location.host.indexOf('stg') > -1 || window.location.host.indexOf('dev') > -1) {
    return 'stage';
  }
  return 'prod';
}

/**
 * Fetches the shipping method mapping JSON file based on environment
 */
async function fetchShippingMapping(): Promise<ShippingMethodMappingData> {
  const env = getEnvironment();
  let data;

  if (env === 'stage') {
    data = await import('../config/shippingMethodMapping.stage.json');
  } else {
    data = await import('../config/shippingMethodMapping.prod.json');
  }
  return data.default as ShippingMethodMappingData;
}

/**
 * Determines the shipping method translation key based on product ID, brand, and locale
 * Priority:
 * 1. Check if productId exists in the brand array
 * 2. Check if locale exists in the default array
 * 3. Fall back to the shipping method with isDefault flag
 * 
 * @param productId - The product ID from the cart
 * @param brand - The brand from the cart (e.g., 'NVIDIA', 'ASUS', 'MSI')
 * @param locale - The locale string (e.g., 'en_GB', 'de_DE')
 * @returns The shipping method translation key or null if not found
 */
export async function getShippingMethodKey(
  productId: number,
  brand: string,
  locale: string
): Promise<string | null> {
  try {
    const data = await fetchShippingMapping();
    const storeData = data[STORE];

    if (!storeData) {
      console.warn(`Store ${STORE} not found in mapping data`);
      return null;
    }

    // Convert locale format from 'en-us' to 'en_US' to match mapping format
    const formattedLocale = locale.replace('-', '_').replace(/^([a-z]{2})_([a-z]{2})$/, (match, lang, country) => {
      return `${lang}_${country.toUpperCase()}`;
    });

    // Priority 1: Check if product ID exists in the brand's product array
    for (const [shippingKey, shippingMethodData] of Object.entries(storeData)) {
      const brandProducts = shippingMethodData[brand];

      // Type guard: ensure it's an array of numbers
      if (brandProducts && Array.isArray(brandProducts) && brandProducts.length > 0 && typeof brandProducts[0] === 'number') {
        if ((brandProducts as number[]).includes(productId)) {
          console.log(
            `Found shipping method ${shippingKey} for productId: ${productId} in brand: ${brand}`
          );
          return shippingKey;
        }
      }
    }

    // Priority 2: Check if locale exists in the default array
    for (const [shippingKey, shippingMethodData] of Object.entries(storeData)) {
      const defaultLocales = shippingMethodData.default;

      if (defaultLocales && Array.isArray(defaultLocales) && defaultLocales.includes(formattedLocale)) {
        console.log(
          `Found shipping method ${shippingKey} for locale: ${formattedLocale} in default array`
        );
        return shippingKey;
      }
    }

    // Priority 3: Fall back to the shipping method with isDefault flag
    for (const [shippingKey, shippingMethodData] of Object.entries(storeData)) {
      if (shippingMethodData.isDefault === true) {
        console.log(
          `Using default shipping method ${shippingKey} for productId: ${productId}, brand: ${brand}, locale: ${formattedLocale}, store: ${STORE}`
        );
        return shippingKey;
      }
    }

    console.warn(
      `No shipping method mapping found for productId: ${productId}, brand: ${brand}, locale: ${formattedLocale}, store: ${STORE}`
    );
    return null;
  } catch (error) {
    console.error('Error getting shipping method key:', error);
    return null;
  }
}

